import React, { useState, useMemo } from 'react';
import { Box, Button, Avatar, Typography, TextField } from '@mui/material';
import { CheckCircle, HelpOutline, Image } from '@mui/icons-material';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';
import { useChatContext } from '../../agent/contexts/ChatContext';
import { useDashboard } from '../../../contexts/DashboardContext';
import { storageService } from '../../../services/storageService';
import BiometricStepsHeader from './BiometricStepsHeader';

interface BiometricDecisionViewProps {
  decisionData: any; // full payload returned by animal register
}

const extractTagFromUrl = (url?: string): string | undefined => {
  if (!url) return undefined;
  const parts = url.split('/');
  return parts[parts.length - 1] || undefined;
};

const toBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip data URL prefix
      const base64 = result.split(',')[1] || result;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const BiometricDecisionView: React.FC<BiometricDecisionViewProps> = ({ decisionData }) => {
  const { sendManagerMessageWithContext } = useChatContext();
  const { sessions, activeSessionId } = useChatContext();
  const { updateContent } = useDashboard();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [base64, setBase64] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [sending, setSending] = useState<boolean>(false);
  const [showUpload, setShowUpload] = useState<boolean>(false);

  const titulo: string = decisionData?.decisao?.titulo || 'Gostaria de cadastrar a biometria do animal?';
  const opcoes: any[] = decisionData?.decisao?.opcoes || [];
  const simOpcao = useMemo(() => opcoes.find(o => String(o.value) === 'true'), [opcoes]);
  const naoOpcao = useMemo(() => opcoes.find(o => String(o.value) === 'false'), [opcoes]);
  const cattleTagFromUrl = useMemo(() => extractTagFromUrl(simOpcao?.url), [simOpcao]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    setError('');
    if (file) {
      try {
        const b64 = await toBase64(file);
        setBase64(b64);
      } catch {
        setError('Falha ao processar a imagem. Tente novamente.');
      }
    } else {
      setBase64('');
    }
  };

  const handleNao = async () => {
    setSending(true);
    setError('');
    try {
      await sendManagerMessageWithContext(
        '',
        'animal',
        {
          cadastro_inserir_biometria: 'false',
          muzzle_biometrics_base64: '',
          cattle_tag: '',
        }
      );
    } catch (err) {
      setError('Erro ao enviar a decisão.');
    } finally {
      setSending(false);
    }
  };

  const handleSim = () => {
    // Navega para a nova tela de recorte biométrico
    const cattleTag = decisionData?.data?.cattle_id?.tag || cattleTagFromUrl || '';
    const animalName = decisionData?.data?.name || '';
    updateContent('biometricCrop', { cattleTag, animalName });
  };

  const handleSalvarBiometria = async () => {
    if (!base64) {
      setError('Selecione uma imagem para cadastrar a biometria.');
      return;
    }
    setSending(true);
    setError('');
    try {
      // Converter base64 (sem prefixo) para Blob JPEG
      const base64ToBlob = (b64: string, mime = 'image/jpeg'): Blob => {
        const byteChars = atob(b64);
        const byteNumbers = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
          byteNumbers[i] = byteChars.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: mime });
      };

      const blob = base64ToBlob(base64);
      const session = sessions.find((s) => s.session_id === activeSessionId);
      const farmId = session?.organization_id || '';
      const cattleTag = decisionData?.data?.cattle_id?.tag || cattleTagFromUrl || '';
      if (!farmId || !cattleTag) {
        throw new Error('Contexto incompleto: farmId ou cattleTag ausente.');
      }

      const uploadRes = await storageService.uploadBiometricImage({
        farmId,
        animalTagOrId: cattleTag,
        file: blob,
        contentType: blob.type || 'image/jpeg',
      });

      await sendManagerMessageWithContext(
        'Cadastrar biometria do animal',
        'animal',
        {
          cadastro_inserir_biometria: 'true',
          muzzle_biometrics_base64: uploadRes.objectKey,
          cattle_tag: cattleTag,
        }
      );
    } catch (err) {
      setError('Erro ao enviar a biometria.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ p: 4, maxWidth: 640, mx: 'auto' }}>
      <BiometricStepsHeader active={0} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Avatar sx={{ bgcolor: DESIGN_TOKENS.colors.primary[600] }}>
          <HelpOutline />
        </Avatar>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {titulo}
        </Typography>
      </Box>

      <Box sx={{ mb: 3, color: DESIGN_TOKENS.colors.text.secondary }}>
        <Typography variant="body2">
          Animal: {decisionData?.data?.name || '-'} | Tag: {decisionData?.data?.cattle_id?.tag || cattleTagFromUrl || '-'}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button
          variant="contained"
          color="success"
          startIcon={<CheckCircle />}
          onClick={handleSim}
          disabled={sending}
        >
          {simOpcao?.label || 'Sim'}
        </Button>
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleNao}
          disabled={sending}
        >
          {naoOpcao?.label || 'Agora não'}
        </Button>
      </Box>

      {showUpload && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar variant="rounded" sx={{ bgcolor: DESIGN_TOKENS.colors.secondary[100], color: DESIGN_TOKENS.colors.secondary[700] }}>
            <Image />
          </Avatar>
          <TextField
            type="file"
            inputProps={{ accept: 'image/*' }}
            onChange={handleFileChange}
            helperText={base64 ? 'Imagem selecionada.' : 'Selecione a imagem do focinho para cadastrar biometria.'}
            sx={{ flex: 1 }}
          />
        </Box>
      )}

      {showUpload && (
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSalvarBiometria}
            disabled={sending || !base64}
          >
            Salvar biometria
          </Button>
        </Box>
      )}

      {error && (
        <Typography variant="body2" sx={{ mt: 2, color: DESIGN_TOKENS.colors.status.error[600] }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default BiometricDecisionView;